/**
 * SxS Network — Garage → Progressier Sync (v3: ride profile keys, 2 Oct 2026)
 * -----------------------------------------
 * Syncs the user's garage data + behavioral signals to Progressier
 * as user properties + tags, so push campaigns can be segmented by:
 *   - Machine make/model/year/trim
 *   - Garage size (1 vs multi-machine = power user signal)
 *   - Whether they've uploaded a photo (engaged signal)
 *   - Last visit, page views, last category viewed
 *   - Cart status, email subscription, viewed event pages
 *
 * Loads on every page. Runs on page load + on every garage change.
 *
 * Deploy: loaded site-wide from snippets/meta-tags.liquid (after garage-data.js).
 * Before 2 Oct 2026 this file existed in Assets but was never included by the theme.
 */

(function() {
  var STORAGE_KEY = 'sxsn_garage_v1';
  var SESSION_KEY = 'sxsn_session_v1';

  // ============================================================
  // HELPERS
  // ============================================================
  function readGarage() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { machines: [], primaryId: null };
      var parsed = JSON.parse(raw);
      if (!parsed.machines) parsed.machines = [];
      return parsed;
    } catch (e) {
      return { machines: [], primaryId: null };
    }
  }

  function readSession() {
    var defaults = {
      first_visit: Date.now(),
      last_visit: Date.now(),
      page_views: 0,
      last_category: null,
      last_product: null,
      categories_count: {},        // {lighting: 5, suspension: 2}
      visited_cart: false,
      visited_checkout: false,
      used_discount: false,
      used_discount_code: null
    };
    try {
      var raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return defaults;
      var parsed = JSON.parse(raw);
      // Merge with defaults so old sessions get new fields
      Object.keys(defaults).forEach(function(k) {
        if (parsed[k] === undefined) parsed[k] = defaults[k];
      });
      return parsed;
    } catch (e) {
      return defaults;
    }
  }

  function writeSession(s) {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch (e) {}
  }

  function slugify(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function uniq(arr) {
    var seen = {}, out = [];
    arr.forEach(function(v) {
      if (v && !seen[v]) { seen[v] = true; out.push(v); }
    });
    return out;
  }

  // ============================================================
  // BUILD PROPERTIES + TAGS FROM GARAGE
  // ============================================================
  function buildPayload() {
    var garage = readGarage();
    var session = readSession();
    var machines = garage.machines || [];
    var primary = machines.find(function(m) { return m.id === garage.primaryId; }) || machines[0] || null;
    var profile = garage.profile || {};

    // Properties: structured key/value data, used for personalization
    var props = {
      // Garage metrics
      garage_count: machines.length,
      garage_filled: machines.length > 0 ? 'yes' : 'no',

      // Primary machine breakdown
      primary_make: primary ? primary.make : '',
      primary_model: primary ? primary.model : '',
      primary_year: primary && primary.year ? String(primary.year) : '',
      primary_trim: primary ? (primary.trim || '') : '',
      primary_nickname: primary ? (primary.nickname || '') : '',
      primary_label: primary ? buildLabel(primary) : '',

      // All machines flattened (for "fits multiple" pushes)
      all_makes: uniq(machines.map(function(m) { return m.make; }).filter(Boolean)).join(','),
      all_models: uniq(machines.map(function(m) { return m.model; }).filter(Boolean)).join(','),
      all_years: uniq(machines.map(function(m) { return m.year ? String(m.year) : ''; }).filter(Boolean)).join(','),

      // Engagement signals
      has_machine_photo: machines.some(function(m) { return !!m.photo_url; }) ? 'yes' : 'no',
      has_build_notes: machines.some(function(m) { return !!m.notes; }) ? 'yes' : 'no',

      // Session / behavior
      first_visit: session.first_visit ? new Date(session.first_visit).toISOString() : '',
      last_visit: session.last_visit ? new Date(session.last_visit).toISOString() : '',
      page_views_total: session.page_views || 0,
      last_category_viewed: session.last_category || '',
      last_product_viewed: session.last_product || '',
      cart_has_items: hasCartItems() ? 'yes' : 'no',

      // Ride profile (v3, 2 Oct 2026) — one key each so segments can be built later; no campaigns now
      ride_use: profile.use || '',
      ride_styles: (profile.styles || []).join(','),
      ride_winter: profile.winter ? 'yes' : 'no',
      ride_trails: (profile.trails || []).concat(profile.other_trail ? [profile.other_trail] : []).join(','),
      ride_interests: (profile.interests || []).join(','),
      ride_maps: (profile.maps || []).join(','),
      ride_profile_filled: hasProfile(profile) ? 'yes' : 'no',
      primary_tires: primary ? (primary.tires || '') : '',
      primary_cage: primary ? (primary.cage || '') : '',
      primary_lift: primary ? (primary.lift || '') : '',
      primary_roof: primary ? (primary.roof || '') : '',
      primary_doors: primary ? (primary.doors || '') : ''
    };

    // Tags: searchable labels for segmentation in Progressier dashboard
    var tags = [];
    if (machines.length > 0) tags.push('garage-filled');
    if (machines.length >= 2) tags.push('multi-machine');
    if (machines.length >= 3) tags.push('garage-power-user');
    if (props.has_machine_photo === 'yes') tags.push('uploaded-photo');
    if (props.has_build_notes === 'yes') tags.push('has-build-notes');
    if (hasCartItems()) tags.push('cart-active');

    // ===== NEW HIGH-VALUE TAGS =====

    // PWA install state
    if (isPwaInstalled()) tags.push('pwa-installed');
    else tags.push('pwa-not-installed');

    // Customer status (logged in vs guest)
    if (isCustomerLoggedIn()) tags.push('is-customer');
    else tags.push('is-prospect');

    // Cart / checkout funnel position
    if (session.visited_cart) tags.push('viewed-cart');
    if (session.visited_checkout) tags.push('viewed-checkout');

    // Discount engagement
    if (session.used_discount) tags.push('discount-clicker');

    // Engagement tier by pageview volume
    var pv = session.page_views || 0;
    if (pv >= 10) tags.push('pageviews:loyal');
    else if (pv >= 3) tags.push('pageviews:casual');
    else tags.push('pageviews:new');

    // Returning vs dormant: how long since their PREVIOUS visit
    // (last_visit_prev is captured before we update last_visit in updateSession)
    if (session.last_visit_prev) {
      var dsince = daysSince(session.last_visit_prev);
      if (dsince >= 30) tags.push('dormant-returning');
      else if (dsince >= 7) tags.push('week-returning');
      else if (dsince <= 1) tags.push('recently-active');
    }

    // Multi-brand garage (cross-sell opportunity)
    var distinctMakes = uniq(machines.map(function(m) { return m.make; }).filter(Boolean));
    if (distinctMakes.length >= 2) tags.push('multi-brand');

    // Top category interest (fires only if visited a category 2+ times)
    var topCat = topInterestCategory(session);
    if (topCat) tags.push('interest:' + slugify(topCat));

    // Page-context tags: where they currently are
    var path = window.location.pathname;
    if (path === '/cart' || path.indexOf('/cart/') === 0) tags.push('on-cart-page');
    if (path.indexOf('/products/') === 0) tags.push('on-product-page');
    if (path.indexOf('/collections/') === 0) tags.push('on-collection-page');

    // Per-machine tags (one entry per machine)
    machines.forEach(function(m) {
      if (m.make) tags.push('make:' + slugify(m.make));
      if (m.model) tags.push('model:' + slugify(m.model));
      if (m.year) tags.push('year:' + m.year);
      if (m.make && m.model) tags.push('rig:' + slugify(m.make + '-' + m.model));
      if (m.make && m.model && m.year) tags.push('rig-yr:' + slugify(m.make + '-' + m.model) + '-' + m.year);
      (m.mods || []).forEach(function(k) { tags.push('has:' + slugify(k)); }); // v7: 'Already on it'
    });

    // Ride-profile tags (v3)
    if (hasProfile(profile)) tags.push('ride-profile-filled');
    if (profile.use) tags.push('ride-use:' + slugify(profile.use));
    (profile.styles || []).forEach(function(s) { tags.push('ride-style:' + slugify(s)); });
    if (profile.winter) tags.push('ride-winter');
    (profile.trails || []).forEach(function(t) { tags.push('ride-trail:' + slugify(t)); });
    (profile.interests || []).forEach(function(i) { tags.push('ride-interest:' + slugify(i)); });
    (profile.maps || []).forEach(function(mp) { tags.push('ride-map:' + slugify(mp)); });

    // Primary-only tag for "for-your-rig" campaigns
    if (primary) {
      tags.push('primary-make:' + slugify(primary.make));
      tags.push('primary-model:' + slugify(primary.model));
    }

    // Page-context tags
    if (window.location.pathname.indexOf('/pages/peak-adrenaline') === 0) {
      tags.push('viewed-peak-adrenaline');
    }
    if (window.location.pathname.indexOf('/pages/get-the-app') === 0) {
      tags.push('viewed-get-the-app');
    }

    return { props: props, tags: uniq(tags) };
  }

  function hasProfile(p) {
    p = p || {};
    return !!(p.use || (p.styles && p.styles.length) || (p.trails && p.trails.length) || (p.maps && p.maps.length) || (p.interests && p.interests.length) || p.winter);
  }

  function buildLabel(m) {
    var parts = [];
    if (m.year) parts.push(m.year);
    if (m.make) parts.push(m.make);
    if (m.model) parts.push(m.model);
    if (m.trim) parts.push(m.trim);
    return parts.join(' ');
  }

  function hasCartItems() {
    try {
      var match = document.cookie.match(/cart=([^;]+)/);
      if (match && match[1] && match[1] !== 'undefined') return true;
    } catch (e) {}
    return false;
  }

  // ============================================================
  // ENHANCED SIGNAL DETECTION
  // ============================================================
  function isPwaInstalled() {
    try {
      return window.matchMedia('(display-mode: standalone)').matches
          || window.navigator.standalone === true
          || document.referrer.indexOf('android-app://') === 0;
    } catch (e) { return false; }
  }

  function isCustomerLoggedIn() {
    // Multi-signal customer detection
    try {
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) return true;
      if (window.__st && window.__st.cid) return true;
      if (document.querySelector('a[href*="/account/logout"]')) return true;
      if (document.querySelector('a[href*="/account"]:not([href*="/login"]):not([href*="/register"])')) {
        // Account link visible (not just login) — likely logged in
        var loginLink = document.querySelector('a[href*="/account/login"], a[href*="/customer_authentication"]');
        // If "Log in" link is present, they're NOT logged in
        if (loginLink) return false;
        return true;
      }
    } catch (e) {}
    return false;
  }

  function topInterestCategory(session) {
    // Find the category with most page views in this session
    var counts = session.categories_count || {};
    var max = 0;
    var top = null;
    Object.keys(counts).forEach(function(k) {
      if (counts[k] > max) { max = counts[k]; top = k; }
    });
    return max >= 2 ? top : null; // Only tag interest if visited 2+ times
  }

  function daysSince(timestamp) {
    if (!timestamp) return 999;
    return Math.floor((Date.now() - timestamp) / (1000 * 60 * 60 * 24));
  }

  // ============================================================
  // SESSION TRACKING (page views, last category, last product)
  // ============================================================
  function updateSession() {
    var session = readSession();

    // Preserve previous last_visit BEFORE updating, so we can compute dormancy
    session.last_visit_prev = session.last_visit || Date.now();
    session.last_visit = Date.now();
    session.page_views = (session.page_views || 0) + 1;

    var path = window.location.pathname;

    // Cart funnel detection
    if (path === '/cart' || path.indexOf('/cart/') === 0) {
      session.visited_cart = true;
    }
    if (path.indexOf('/checkouts/') === 0 || path === '/checkout') {
      session.visited_checkout = true;
    }

    // Discount param tracking (sticky once detected)
    var urlParams = new URLSearchParams(window.location.search);
    var discount = urlParams.get('discount');
    if (discount) {
      session.used_discount = true;
      session.used_discount_code = discount;
    }
    // Also catch /discount/CODE redirect URLs
    if (path.indexOf('/discount/') === 0) {
      session.used_discount = true;
      session.used_discount_code = path.split('/')[2] || session.used_discount_code;
    }

    // Category interest tracking
    if (path.indexOf('/collections/') === 0) {
      var cat = path.split('/')[2] || '';
      if (cat) {
        session.last_category = cat;
        session.categories_count = session.categories_count || {};
        session.categories_count[cat] = (session.categories_count[cat] || 0) + 1;
      }
    }
    if (path.indexOf('/products/') === 0) {
      session.last_product = path.split('/')[2] || '';
    }

    writeSession(session);
  }

  // ============================================================
  // PROGRESSIER SYNC
  // ============================================================
  function syncToProgressier() {
    if (!window.progressier || typeof window.progressier.add !== 'function') {
      // Progressier hasn't loaded yet — retry in a moment
      return;
    }
    try {
      var payload = buildPayload();
      // Push user properties + tags to Progressier
      // Their .add() merges with existing user data
      var add = { properties: payload.props, tags: payload.tags };
      // v7: anonymous devices get a stable id (sxsn_device_id) so a push can target one device;
      // logged-in customers are linked by meta-tags.liquid (id = Shopify customer id, email) instead.
      if (!window.SxSNCustomer) {
        try {
          var did = localStorage.getItem('sxsn_device_id');
          if (!did) { did = 'dev_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); localStorage.setItem('sxsn_device_id', did); }
          add.id = did;
        } catch (e) {}
      }
      window.progressier.add(add);
    } catch (e) {
      // Silent fail — don't block the page
    }
  }

  // ============================================================
  // INIT
  // ============================================================
  function waitForProgressier(callback, attemptsLeft) {
    if (window.progressier && typeof window.progressier.add === 'function') {
      callback();
      return;
    }
    if (attemptsLeft <= 0) return;
    setTimeout(function() { waitForProgressier(callback, attemptsLeft - 1); }, 500);
  }

  function init() {
    updateSession();
    waitForProgressier(syncToProgressier, 20); // up to 10s of retries
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync when garage changes (same tab or other tab)
  window.addEventListener('sxsn:garage-changed', syncToProgressier);
  window.addEventListener('storage', function(e) {
    if (e.key === STORAGE_KEY) syncToProgressier();
  });

  // Expose for manual triggering
  window.SxSNProgressierSync = {
    sync: syncToProgressier,
    payload: buildPayload
  };
})();
